import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  FormGroup,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import {
  PlayArrow as RunIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as PassIcon,
  Cancel as FailIcon,
  Error as ErrorIcon,
  ArrowBack as BackIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const ENVIRONMENT_PRESETS = [
  {
    id: 'local',
    label: 'Local Dev',
    apiUrl: 'http://localhost:8000',
    frontendUrl: 'http://localhost:3000',
  },
  {
    id: 'deployed',
    label: 'Deployed',
    apiUrl: 'https://api.outcomes.ids-labs.dev.pub.cambridgeassociates.cloud',
    frontendUrl: 'https://outcomes.ids-labs.dev.pub.cambridgeassociates.cloud',
  },
];

function deriveInitialFrontendUrl(apiUrl) {
  const preset = ENVIRONMENT_PRESETS.find((p) => p.apiUrl === apiUrl);
  if (preset) return preset.frontendUrl;
  if (apiUrl.includes('localhost:8000')) return 'http://localhost:3000';
  if (apiUrl.includes('127.0.0.1:8000')) return 'http://127.0.0.1:3000';
  return typeof window !== 'undefined' ? window.location.origin : apiUrl;
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
      <IconButton size="small" onClick={handleCopy} sx={{ ml: 0.5, opacity: 0.7 }}>
        <CopyIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );
}

const SUITE_OPTIONS = [
  { id: 'quick', label: 'Quick Health & Smoke' },
  { id: 'qa_core', label: 'QA Core Workflow' },
  { id: 'comprehensive', label: 'Comprehensive CRUD' },
  { id: 'playwright', label: 'Playwright E2E' },
  { id: 'api_coverage', label: 'API Coverage' },
];

function statusChip(status, small) {
  const props = { size: small ? 'small' : 'medium', variant: 'filled' };
  if (status === 'pass') return <Chip {...props} label="PASS" color="success" icon={<PassIcon />} />;
  if (status === 'fail') return <Chip {...props} label="FAIL" color="error" icon={<FailIcon />} />;
  return <Chip {...props} label="ERROR" color="warning" icon={<ErrorIcon />} />;
}

function durationLabel(ms) {
  if (ms == null) return null;
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
}

function formatRelativeTime(isoString) {
  if (!isoString) return null;
  const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

export default function RegressionDashboard({ onBackToHome }) {
  const [selectedSuites, setSelectedSuites] = useState(
    SUITE_OPTIONS.reduce((acc, s) => ({ ...acc, [s.id]: true }), {})
  );
  const [targetApiUrl, setTargetApiUrl] = useState(API_BASE);
  const [targetFrontendUrl, setTargetFrontendUrl] = useState(() => deriveInitialFrontendUrl(API_BASE));
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [preCheckStatus, setPreCheckStatus] = useState(null); // null | 'checking' | 'ok' | 'error'
  const [preCheckMsg, setPreCheckMsg] = useState('');
  const [error, setError] = useState('');

  const toggleSuite = (id) =>
    setSelectedSuites((prev) => ({ ...prev, [id]: !prev[id] }));

  const runPreChecks = useCallback(async () => {
    setPreCheckStatus('checking');
    setPreCheckMsg(`Checking ${targetApiUrl}/health...`);
    try {
      const health = await fetch(`${targetApiUrl}/health`);
      if (!health.ok) throw new Error(`/health returned ${health.status}`);
      setPreCheckMsg('Checking storage config...');
      const storage = await fetch(`${targetApiUrl}/api/storage-info`);
      if (!storage.ok) throw new Error(`/api/storage-info returned ${storage.status}`);
      setPreCheckStatus('ok');
      setPreCheckMsg(`${targetApiUrl} reachable, storage configured`);
      return true;
    } catch (err) {
      setPreCheckStatus('error');
      setPreCheckMsg(err.message);
      return false;
    }
  }, [targetApiUrl]);

  const handleRun = useCallback(async () => {
    setError('');
    setResult(null);
    const suites = SUITE_OPTIONS.filter((s) => selectedSuites[s.id]).map((s) => s.id);
    if (suites.length === 0) {
      setError('Select at least one suite to run.');
      return;
    }

    const preOk = await runPreChecks();
    if (!preOk) return;

    setRunning(true);
    try {
      const resp = await fetch(`${API_BASE}/api/dev/run-tests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          suites,
          api_url: targetApiUrl,
          frontend_url: targetFrontendUrl,
        }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`Server error ${resp.status}: ${text.slice(0, 200)}`);
      }
      const data = await resp.json();
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setRunning(false);
    }
  }, [selectedSuites, runPreChecks, targetApiUrl, targetFrontendUrl]);

  // Summary stats
  const summary = result
    ? result.suites.reduce(
        (acc, suite) => {
          acc.total += suite.tests.length;
          acc.passing += suite.tests.filter((t) => t.status === 'pass').length;
          if (suite.status !== 'pass') acc.failedSuites += 1;
          return acc;
        },
        { total: 0, passing: 0, failedSuites: 0 }
      )
    : null;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        {onBackToHome && (
          <Button variant="outlined" startIcon={<BackIcon />} onClick={onBackToHome}>
            Home
          </Button>
        )}
        <Typography variant="h5" fontWeight={600}>
          Regression Test Suite
        </Typography>
      </Box>

      {/* Environment selector */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          Target Environment
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            Preset:
          </Typography>
          <ButtonGroup size="small" disabled={running}>
            {ENVIRONMENT_PRESETS.map((preset) => {
              const active = targetApiUrl === preset.apiUrl && targetFrontendUrl === preset.frontendUrl;
              return (
                <Button
                  key={preset.id}
                  variant={active ? 'contained' : 'outlined'}
                  onClick={() => {
                    setTargetApiUrl(preset.apiUrl);
                    setTargetFrontendUrl(preset.frontendUrl);
                  }}
                >
                  {preset.label}
                </Button>
              );
            })}
          </ButtonGroup>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="API URL"
            size="small"
            value={targetApiUrl}
            onChange={(e) => setTargetApiUrl(e.target.value)}
            disabled={running}
            sx={{ flex: '1 1 280px' }}
            placeholder="http://localhost:8000"
          />
          <TextField
            label="Frontend URL"
            size="small"
            value={targetFrontendUrl}
            onChange={(e) => setTargetFrontendUrl(e.target.value)}
            disabled={running}
            sx={{ flex: '1 1 280px' }}
            placeholder="http://localhost:3000"
            helperText="Used by Playwright E2E"
          />
        </Box>
      </Paper>

      {/* Suite selector + Run button */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <FormGroup row>
            {SUITE_OPTIONS.map((s) => (
              <FormControlLabel
                key={s.id}
                control={
                  <Checkbox
                    checked={selectedSuites[s.id]}
                    onChange={() => toggleSuite(s.id)}
                    disabled={running}
                    size="small"
                  />
                }
                label={s.label}
              />
            ))}
          </FormGroup>
          <Box sx={{ ml: 'auto' }}>
            <Button
              variant="contained"
              startIcon={running ? <CircularProgress size={16} color="inherit" /> : <RunIcon />}
              onClick={handleRun}
              disabled={running}
            >
              {running ? 'Running...' : 'Run Tests'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Pre-check status */}
      {preCheckStatus && (
        <Alert
          severity={
            preCheckStatus === 'checking' ? 'info' : preCheckStatus === 'ok' ? 'success' : 'error'
          }
          sx={{ mb: 2 }}
          icon={preCheckStatus === 'checking' ? <CircularProgress size={16} /> : undefined}
        >
          {preCheckMsg}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Summary bar */}
      {result && summary && (
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Last run: {formatRelativeTime(result.started_at)}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {summary.passing} / {summary.total} passing
          </Typography>
          {summary.failedSuites > 0 && (
            <Chip label={`${summary.failedSuites} FAILED`} color="error" size="small" />
          )}
          {summary.failedSuites === 0 && (
            <Chip label="ALL PASS" color="success" size="small" />
          )}
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {durationLabel(result.duration_ms)} total
          </Typography>
        </Paper>
      )}

      {/* Suite accordions */}
      {result &&
        result.suites.map((suite) => {
          const passCount = suite.tests.filter((t) => t.status === 'pass').length;
          return (
            <Accordion key={suite.id} defaultExpanded={suite.status !== 'pass'}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', pr: 1 }}>
                  {statusChip(suite.status)}
                  <Typography fontWeight={500}>{suite.label}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    ({passCount}/{suite.tests.length})
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                    {durationLabel(suite.duration_ms)}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <Table size="small">
                  <TableBody>
                    {suite.tests.map((test, idx) => (
                      <React.Fragment key={idx}>
                        <TableRow
                          sx={{
                            bgcolor:
                              test.status === 'fail'
                                ? 'error.light'
                                : test.status === 'error'
                                ? 'warning.light'
                                : 'transparent',
                          }}
                        >
                          <TableCell sx={{ width: 80, py: 0.5 }}>
                            {statusChip(test.status, true)}
                          </TableCell>
                          <TableCell sx={{ py: 0.5 }}>
                            <Typography variant="body2">{test.name}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ width: 70, py: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {durationLabel(test.duration_ms)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        {(test.detail || test.screenshot_b64) && (
                          <TableRow>
                            <TableCell colSpan={3} sx={{ py: 0.5, pl: 4, bgcolor: 'action.hover' }}>
                              {test.detail && (
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                  <Typography
                                    variant="caption"
                                    component="pre"
                                    sx={{
                                      fontFamily: 'monospace',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-all',
                                      color: 'error.main',
                                      m: 0,
                                      flex: 1,
                                    }}
                                  >
                                    {test.detail}
                                  </Typography>
                                  <CopyButton text={test.detail} />
                                </Box>
                              )}
                              {test.screenshot_b64 && (
                                <Box sx={{ mt: 1 }}>
                                  <img
                                    src={`data:image/png;base64,${test.screenshot_b64}`}
                                    alt="failure screenshot"
                                    style={{ maxWidth: '100%', maxHeight: 300, border: '1px solid #ccc' }}
                                  />
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
                {suite.raw_output && (
                  <Collapse in>
                    <Box sx={{ p: 1.5, bgcolor: 'grey.900' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'grey.500', fontWeight: 600 }}>
                          Raw output
                        </Typography>
                        <CopyButton text={suite.raw_output} />
                      </Box>
                      <Typography
                        variant="caption"
                        component="pre"
                        sx={{
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                          color: 'grey.300',
                          m: 0,
                        }}
                      >
                        {suite.raw_output}
                      </Typography>
                    </Box>
                  </Collapse>
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
    </Box>
  );
}
